import { useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useAction, useAsync } from '../lib/useAsync.js';
import {
  MAPPED_FORMS,
  SHOW_ON,
  SHOW_ON_LABELS,
  byPosition,
  categoryOptions,
  categoryText,
  productOptions,
  reorder,
  ruleInput,
  showOnOf,
  topicsFor,
  type Category,
  type CategoryLabel,
  type CategoryRule,
  type ShowOn,
  type SupportApi,
} from '../lib/support-api.js';
import { Menu, MenuItem, Screen } from '../components/Screen.js';
import { Select } from '../components/Select.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { AsyncBoundary, Button, EmptyState, Note, OutcomeNote, Panel, Table, Td, inputStyle } from '../components/ui.js';

/**
 * Support > Form mapping (support#68, Jon 2026-10-11): what a choice on a
 * support form files a new ticket under.
 *
 * Both forms ask what the person needs help with, per product. A rule maps a
 * selection (or any) on a form, for a product (or any), to a category, and the
 * most specific rule wins. Lives in Support > Configuration.
 *
 * Since auth#672 the rules are also the forms' topic lists: "Shows on" says
 * which form(s) list the topic, the order is the list order, and a topic may
 * show before it files anywhere ("No category yet"). The "What members see"
 * panel is the list each form loads, so staff can check it without opening
 * the forms.
 */
type Draft = {
  id?: string;
  showOn: ShowOn;
  position: string;
  product: string | null;
  topic: string | null;
  categoryId: string;
};

export function SupportMappingScreen({ api, tabs }: { api: SupportApi; tabs: ReactNode }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const { state, reload } = useAsync(() => api.categoryRules(), []);
  const move = useAction();
  const tree = useAsync(() => api.categories(), []);
  const products = useAsync(() => api.products(), []);
  const productChoices = products.state.status === 'ready' ? productOptions(products.state.data.products) : [];
  const productLabel = (slug: string | null) =>
    slug === null ? 'Any product' : (productChoices.find((p) => p.value === slug)?.label.replace(/ \(.*\)$/, '') ?? slug);

  return (
    <Screen filters={tabs}>
      <AsyncBoundary state={state}>
        {(d) => {
          const rules = byPosition(d.rules);
          const open = (rule: CategoryRule) =>
            setDraft({
              id: rule.id,
              showOn: showOnOf(rule),
              position: rule.position === undefined ? '' : String(rule.position),
              product: rule.product,
              topic: rule.topic,
              categoryId: rule.category?.id ?? '',
            });
          // One place up or down: renumbers the list and writes only the rules
          // whose position changed (usually two).
          const shift = (rule: CategoryRule, direction: 'up' | 'down') =>
            void move
              .run(async () => {
                for (const w of reorder(rules, rule.id, direction)) {
                  await api.updateCategoryRule(w.rule.id, ruleInput(w.rule, w.position));
                }
                return direction === 'up' ? 'Moved up.' : 'Moved down.';
              })
              .then(() => reload(true));

          return (
            <>
              {draft && tree.state.status === 'ready' ? (
                <RuleModal
                  api={api}
                  draft={draft}
                  categories={tree.state.data.categories}
                  productOptions={productChoices}
                  onDone={() => {
                    setDraft(null);
                    reload(true);
                  }}
                  onClose={() => setDraft(null)}
                />
              ) : null}

              <Panel
                title="Form mapping"
                action={
                  d.canEdit ? (
                    <Button onClick={() => setDraft({ showOn: 'BOTH', position: '', product: null, topic: null, categoryId: '' })}>
                      Add rule
                    </Button>
                  ) : undefined
                }
              >
                {move.outcome && !move.outcome.ok ? <OutcomeNote outcome={move.outcome} /> : null}
                {rules.length === 0 ? (
                  <EmptyState title="No rules yet." suggestion="Tickets from the forms will need a category until there are." />
                ) : (
                  <Table columns={['Order', 'Shows on', 'Product', 'Selection', 'Files under', '']} actionsLast>
                    {rules.map((r, i) => (
                      <tr key={r.id} onDoubleClick={d.canEdit ? () => open(r) : undefined} style={d.canEdit ? st.row : undefined}>
                        <Td>
                          <span style={st.order}>
                            <span style={{ minWidth: '1.6rem' }}>{r.position ?? <span style={st.muted}>none</span>}</span>
                            {d.canEdit ? (
                              <>
                                <OrderButton
                                  label={`Move ${r.topic ?? 'this rule'} up`}
                                  disabled={move.running || i === 0}
                                  onClick={() => shift(r, 'up')}
                                >
                                  ↑
                                </OrderButton>
                                <OrderButton
                                  label={`Move ${r.topic ?? 'this rule'} down`}
                                  disabled={move.running || i === rules.length - 1}
                                  onClick={() => shift(r, 'down')}
                                >
                                  ↓
                                </OrderButton>
                              </>
                            ) : null}
                          </span>
                        </Td>
                        <Td>{SHOW_ON_LABELS[showOnOf(r)]}</Td>
                        <Td>{r.product === null ? <span style={st.muted}>Any product</span> : productLabel(r.product)}</Td>
                        <Td>{r.topic ?? <span style={st.muted}>Any selection</span>}</Td>
                        <Td>
                          <CategoryName category={r.category} />
                        </Td>
                        <Td action>
                          {d.canEdit ? <RowMenu label={`Actions for ${r.topic ?? 'this rule'}`} item="Details" onSelect={() => open(r)} /> : null}
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Panel>

              <WhatMembersSee rules={d.rules} products={productChoices} />

              {!d.canEdit ? <Note>Only a support admin or global admin can change the form mapping.</Note> : null}
            </>
          );
        }}
      </AsyncBoundary>
    </Screen>
  );
}

/** The "…" menu on a row, with its one action. */
function RowMenu({ label, item, onSelect }: { label: string; item: string; onSelect: () => void }) {
  return (
    <Menu label={label}>
      {(close) => (
        <MenuItem
          onClick={() => {
            close();
            onSelect();
          }}
        >
          {item}
        </MenuItem>
      )}
    </Menu>
  );
}

function OrderButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rw-press"
      style={{ ...st.orderButton, opacity: disabled ? 0.35 : 1, cursor: disabled ? 'default' : 'pointer' }}
    >
      {children}
    </button>
  );
}

/**
 * Each form's topic list as members see it, for one product at a time. Built
 * from the rules on screen the way support's `GET /topics` builds it, so a
 * change shows here as soon as it is saved.
 */
function WhatMembersSee({ rules, products }: { rules: CategoryRule[]; products: { value: string; label: string }[] }) {
  const [product, setProduct] = useState('');
  return (
    <Panel
      title="What members see"
      action={
        <Select
          label="Product"
          size="sm"
          align="end"
          value={product}
          onChange={setProduct}
          options={[{ value: '', label: 'Any product' }, ...products]}
        />
      }
    >
      <div style={st.lists}>
        {MAPPED_FORMS.map((form) => (
          <div key={form} style={st.list}>
            <div style={st.listTitle}>{SHOW_ON_LABELS[form]}</div>
            <ol style={st.topics}>
              {topicsFor(rules, form, product || null).map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function CategoryName({ category }: { category: CategoryLabel | null }) {
  if (!category) return <span style={st.needs}>No category yet</span>;
  const parents = category.trail.slice(0, -1);
  return (
    <div title={categoryText(category)}>
      <div style={{ fontWeight: 600 }}>{category.name}</div>
      {parents.length ? <div style={st.trail}>{parents.join(' › ')}</div> : null}
    </div>
  );
}

function RuleModal({
  api,
  draft,
  categories,
  productOptions: products,
  onDone,
  onClose,
}: {
  api: SupportApi;
  draft: Draft;
  categories: Category[];
  productOptions: { value: string; label: string }[];
  onDone: () => void;
  onClose: () => void;
}) {
  const [showOn, setShowOn] = useState<ShowOn>(draft.showOn);
  const [position, setPosition] = useState(draft.position);
  const [product, setProduct] = useState(draft.product ?? '');
  const [topic, setTopic] = useState(draft.topic ?? '');
  const [categoryId, setCategoryId] = useState(draft.categoryId);
  const action = useAction();
  const editing = Boolean(draft.id);

  // Only categories offered on the product chosen; any product shows the ones for every product.
  const choices = categoryOptions(categories, product || undefined).filter(
    (o) => product || categories.find((c) => c.id === o.value)?.products.length === 0,
  );

  const body = () => ({
    showOn,
    // Empty leaves it to support, which puts a new rule last.
    ...(position.trim() === '' ? {} : { position: Number(position) }),
    product: product || null,
    topic: topic.trim() || null,
    // A topic may show on a form before it files anywhere (auth#672).
    categoryId: categoryId || null,
  });

  const save = () =>
    void action
      .run(async () => {
        if (draft.id) await api.updateCategoryRule(draft.id, body());
        else await api.createCategoryRule(body());
        return 'Saved.';
      })
      .then((ok) => {
        if (ok) onDone();
      });

  const remove = () =>
    void action
      .run(async () => {
        await api.deleteCategoryRule(draft.id!);
        return 'Rule deleted.';
      })
      .then((ok) => {
        if (ok) onDone();
      });

  return (
    <Modal
      title={editing ? 'Rule details' : 'Add rule'}
      subtitle="New tickets from this form and selection are filed under the category. Tickets already filed keep theirs."
      onClose={onClose}
      width={540}
      footer={
        <ModalActions>
          {editing ? (
            <span style={{ marginRight: 'auto' }}>
              <Button tone="danger" disabled={action.running} onClick={remove}>
                Delete rule
              </Button>
            </span>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || (!topic.trim() && !categoryId)}
            title={!topic.trim() && !categoryId ? 'A rule for any selection lists nothing, so it needs a category.' : undefined}
            onClick={save}
          >
            Save
          </Button>
        </ModalActions>
      }
    >
      <div style={st.form}>
        <Field label="Shows on" hint="Which support form lists this selection, and whose tickets this rule files.">
          <Select<ShowOn>
            label="Shows on"
            value={showOn}
            onChange={setShowOn}
            options={SHOW_ON.map((f) => ({ value: f, label: SHOW_ON_LABELS[f] }))}
          />
        </Field>
        <Field label="Product">
          <Select label="Product" value={product} onChange={setProduct} options={[{ value: '', label: 'Any product' }, ...products]} />
        </Field>
        <Field
          label="Selection"
          hint="Exactly as the form shows it, such as I cannot sign in. Leave empty for any selection, which adds nothing to the list."
        >
          <input
            aria-label="Selection"
            value={topic}
            maxLength={200}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Any selection"
            style={{ ...inputStyle, width: '100%' }}
          />
        </Field>
        <Field label="Order" hint="Where the selection sits in the form's list, lowest first. Leave empty to put a new one last.">
          <input
            aria-label="Order"
            inputMode="numeric"
            value={position}
            onChange={(e) => setPosition(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="Last"
            style={{ ...inputStyle, width: '8rem' }}
          />
        </Field>
        <Field
          label="Files under"
          hint="With no category yet the selection still shows on the form, and its tickets wait under Needs category."
        >
          <Select
            label="Files under"
            value={categoryId}
            onChange={setCategoryId}
            options={[{ value: '', label: 'No category yet' }, ...choices]}
          />
        </Field>
      </div>
      <OutcomeNote outcome={action.outcome} />
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}>
      <span style={{ fontWeight: 600, color: C.textMid }}>{label}</span>
      {children}
      {hint ? <span style={{ color: C.muted, fontSize: '.8rem' }}>{hint}</span> : null}
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  muted: { color: C.muted },
  row: { cursor: 'default', userSelect: 'none' },
  trail: { fontSize: '.8rem', color: C.textMid, marginTop: '.1rem' },
  needs: { color: C.warnText, fontWeight: 600, fontSize: '.85rem' },
  form: { display: 'grid', gap: '1rem' },
  order: { display: 'inline-flex', alignItems: 'center', gap: '.25rem', fontVariantNumeric: 'tabular-nums' },
  orderButton: {
    background: 'none',
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.textMid,
    width: 24,
    height: 24,
    lineHeight: 1,
    padding: 0,
    fontSize: '.8rem',
  },
  lists: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem 1.5rem', padding: '1rem 1.2rem' },
  list: { display: 'grid', gap: '.4rem', alignContent: 'start' },
  listTitle: { fontWeight: 650, fontSize: '.9rem' },
  topics: { margin: 0, paddingLeft: '1.3rem', display: 'grid', gap: '.25rem', fontSize: '.88rem' },
};
